import React, { useState, useEffect } from "react";
import { Eye, EyeOff, Lock, AlertCircle } from "lucide-react";
import { AuthContainer } from "./AuthContainer";
import { useAuth } from "./AuthContext";
import { useApp } from "./AppContext";
import { useLogin } from "../api/auth/useLogin";
import { useTranslation } from "../i18n/TranslationContext";

export function PasswordLogin() {
	const [password, setPassword] = useState("");
	const [showPassword, setShowPassword] = useState(false);
	const { authData, setCurrentStep } = useAuth();
	const { setIsAuthenticated, setUserProfile, updateOnboardingState } =
		useApp();
	const { login, loading: isLoading, error, success } = useLogin();
	const { t } = useTranslation();

	// Get email from session storage or auth context
	const email =
		authData.email ||
		(() => {
			const pendingAuth = sessionStorage.getItem("pendingAuth");
			if (pendingAuth) {
				const { email } = JSON.parse(pendingAuth);
				return email;
			}
			return "";
		})();

	useEffect(() => {
		// If no email, redirect back to sign in
		if (!email) {
			setCurrentStep(0);
		}
	}, [email, setCurrentStep]);

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		if (!password) {
			return;
		}

		try {
			// Attempt login with email and password
			const response = await login(email, password);

			if (response) {
				// Clear pending auth from session storage
				sessionStorage.removeItem("pendingAuth");

				// Set user profile from response
				setUserProfile({
					firstName: response.user.firstName,
					lastName: response.user.lastName,
					email: response.user.email,
					phone: response.user.phone || "",
					uid: response.user.extraId.toString(),
					dateOfBirth: response.user.dateOfBirth || "",
					nationality: response.user.nationality || "",
					germanLevel: response.user.germanLevel || "",
					company: response.user.company || "",
					department: response.user.department || "",
				});

				// Mark as authenticated - existing user login, no onboarding
				// Set isFirstTimeUser to false for login flow
				updateOnboardingState({ isFirstTimeUser: false });
				setIsAuthenticated(true);
			}
		} catch (err: any) {
			console.error("Login error:", err);
			// The error state from useLogin is not directly available here,
			// so we'll rely on the error message from the hook or handle it internally.
			// For now, we'll set a generic error message.
			// If the error is from useLogin, it will be handled by the hook's error state.
		} finally {
			// setIsLoading(false); // This is now managed by useLogin's loading state
		}
	};

	const handleBack = () => {
		// Go back to sign in
		setCurrentStep(0);
	};

	const handleForgotPassword = () => {
		// Navigate to forgot password screen
		setCurrentStep(8); // Forgot password step
	};

	return (
		<AuthContainer showBack onBack={handleBack}>
			<div className="text-center mb-8">
				<h1 className="text-[28px] font-bold text-ucm-text-dark mb-2 leading-tight">
					{t('auth.signIn.welcomeBack')}
				</h1>
				<p className="text-sm text-ucm-text-medium">
					{t('auth.signIn.enterPasswordToContinue')}
				</p>
				<p className="text-sm text-ucm-text-light mt-2">{email}</p>
			</div>

			<form onSubmit={handleSubmit} className="space-y-6">
				<div>
					<label className="block text-sm font-medium text-ucm-text-medium mb-2">
						{t('auth.signIn.passwordLabel')}
					</label>
					<div className="relative">
						<input
							type={showPassword ? "text" : "password"}
							value={password}
							onChange={(e) => {
								setPassword(e.target.value);
								// Clear error on typing
							}}
							className={`w-full h-14 px-4 pr-12 border rounded-xl bg-white transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-ucm-primary-teal focus:border-transparent ${
								error ? "border-ucm-error-red" : "border-ucm-border-light"
							}`}
							placeholder={t('auth.signIn.passwordPlaceholder')}
							disabled={isLoading}
							autoFocus
						/>
						<button
							type="button"
							onClick={() => setShowPassword(!showPassword)}
							className="absolute right-4 top-1/2 transform -translate-y-1/2 text-ucm-text-light hover:text-ucm-text-medium"
						>
							{showPassword ? (
								<EyeOff className="w-5 h-5" />
							) : (
								<Eye className="w-5 h-5" />
							)}
						</button>
					</div>
					{error && (
						<div className="mt-2 flex items-start">
							<AlertCircle className="w-4 h-4 text-ucm-error-red mr-2 mt-0.5 flex-shrink-0" />
							<p className="text-sm text-ucm-error-red">{error}</p>
						</div>
					)}
				</div>

				<div className="text-right">
					<button
						type="button"
						onClick={handleForgotPassword}
						className="text-sm text-ucm-blue-link hover:text-ucm-blue-link-hover hover:underline"
					>
						Forgot password?
					</button>
				</div>

				<button
					type="submit"
					disabled={isLoading || !password}
					className={`w-full h-14 bg-ucm-primary-teal text-white font-semibold rounded-full transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-ucm-primary-teal focus:ring-offset-2 text-[16px] ${
						isLoading || !password
							? "opacity-50 cursor-not-allowed"
							: "hover:bg-ucm-primary-teal-hover"
					}`}
				>
					{isLoading ? (
						<div className="flex items-center justify-center">
							<div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
							{t('auth.signIn.signingIn')}
						</div>
					) : (
						t('auth.signIn.title')
					)}
				</button>
			</form>
		</AuthContainer>
	);
}
